import * as Sentry from "@sentry/browser";
import { isAppError } from "@shared/app-error";

/**
 * §4.3 / §5.5: Sentry I/O 境界。 init / captureException を本ファイルに集約し、
 * `@sentry/browser` への直接依存は本ファイル 1 つに閉じる (CLAUDE.md ディレクトリ規律
 * 「infra/ は I/O 境界の primitive」 に整合)。 DSN 未設定 (開発時 / e2e) では SDK が
 * 自動的に no-op になるため、 `reportToSentry` も silent に通る。
 *
 * 必須設定:
 * - sendDefaultPii: false (PII 最小化)
 * - replayIntegration: blockAllMedia + maskAllText に加えて block: ['canvas']
 *   (uPlot で描画される MAR グラフ Canvas は blockAllMedia の対象外なので明示する)
 * - replaysOnErrorSampleRate: 0.1 (サンプリングを抑える)
 *
 * beforeSend で以下を strip / 移送する (要件 §4.3 PII 最小化):
 * - AppError 由来の cause を `extra.appErrorCause` に文字列化して移送し、
 *   生 Error / DOMException が exception.values 経由で詳細送信されないようにする
 * - stacktrace.frames[].filename と request.url からユーザー名を含むパス・
 *   `?token=` 等のクエリパラメータを strip
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        blockAllMedia: true,
        maskAllText: true,
        block: ["canvas"],
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    beforeSend(event, hint) {
      hoistAppErrorCause(event, hint);
      stripPathLikeFields(event);
      return event;
    },
  });
}

/**
 * Sentry へのエラー送信。 `handleAppError` から呼ばれる単一経路。 caller 側で `tags` 等を
 * 渡したいケース (AppError.code / policy) のため、 SDK の `captureException` 第 2 引数の
 * `tags` だけ受け付ける minimal API。 他のオプション (extra / context 等) は YAGNI で省略。
 */
export function reportToSentry(err: unknown, opts?: { tags?: Record<string, string> }): void {
  if (opts?.tags) {
    Sentry.captureException(err, { tags: opts.tags });
  } else {
    Sentry.captureException(err);
  }
}

/**
 * AppError(cause) の cause は Error の `cause` プロパティに入るが、Sentry は
 * デフォルトでは cause チェーンを exception.values 配列に展開してしまう。生 Error の
 * message が PII (ユーザー入力, OS パス等) を含む可能性があるため、`extra` にだけ
 * 短い文字列として残し、exception.values からは元の AppError 1 段だけに整える。
 */
function hoistAppErrorCause(event: Sentry.ErrorEvent, hint: Sentry.EventHint): void {
  const original = hint.originalException;
  if (!isAppError(original)) return;
  const cause = (original as { cause?: unknown }).cause;
  if (cause === undefined) return;

  event.extra = {
    ...event.extra,
    appErrorCause: causeSummary(cause),
  };

  if (event.exception?.values && event.exception.values.length > 1) {
    event.exception.values = event.exception.values.slice(0, 1);
  }
}

function causeSummary(cause: unknown): string {
  if (cause instanceof Error) {
    return `${cause.name}: ${cause.message}`;
  }
  if (typeof cause === "string") return cause;
  try {
    return JSON.stringify(cause);
  } catch {
    return Object.prototype.toString.call(cause);
  }
}

const PATH_LIKE_RE = /(file:\/\/|https?:\/\/|\/Users\/|\/home\/|[A-Za-z]:\\)\S+/g;

function scrubPathLike(value: string): string {
  return value.replace(PATH_LIKE_RE, (match) => {
    // ファイル名 (basename) だけ残し、ディレクトリ部分は削る
    const sep = match.lastIndexOf("/") >= 0 ? "/" : "\\";
    const basename = match.slice(match.lastIndexOf(sep) + 1);
    return `<scrubbed>/${basename.split(/[?#]/)[0]}`;
  });
}

function stripPathLikeFields(event: Sentry.ErrorEvent): void {
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubPathLike(ex.value);
      const frames = ex.stacktrace?.frames;
      if (frames) {
        for (const frame of frames) {
          if (frame.filename) frame.filename = scrubPathLike(frame.filename);
          if (frame.abs_path) frame.abs_path = scrubPathLike(frame.abs_path);
        }
      }
    }
  }
  if (event.request?.url) {
    event.request.url = scrubPathLike(event.request.url);
  }
}
